import { useEffect } from "react";
import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";
import { parseMermaid } from "../src/parseMermaid";
import { graphToExcalidraw } from "../src/graphToExcalidraw";
import { convertToExcalidrawElements } from "@excalidraw/excalidraw";
import { DEFAULT_FONT_SIZE } from "../src/constants";

export const useSagamudaMermaidModal = (api: ExcalidrawImperativeAPI | null) => {
  useEffect(() => {
    if (!api) return;

    const interval = setInterval(() => {
      // 1. Replace any text node containing 'Mermaid to Excalidraw' globally
      const walk = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, null);
      let n;
      while ((n = walk.nextNode())) {
        if (n.textContent && n.textContent.includes('Mermaid to Excalidraw')) {
          n.textContent = n.textContent.replace('Mermaid to Excalidraw', 'draw.sagamuda.id');
        }
      }

      // 2. Replace description text
      const pWalk = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, null);
      while ((n = pWalk.nextNode())) {
        if (n.textContent && n.textContent.includes('Currently only Flowchart')) {
          const parent = n.parentElement;
          if (parent && !parent.innerHTML.includes('draw.sagamuda.id')) {
            parent.innerHTML = 'Welcome to <strong>draw.sagamuda.id</strong>. Tulis syntax Markdown atau Mermaid di sini untuk menggenerate ke kanvas secara cerdas di area yang kosong (bebas dari tumpang tindih).';
          }
        }
      }

      // 3. Intercept "Insert" button
      const buttons = document.querySelectorAll('button');
      let insertBtn: any = null;
      buttons.forEach(btn => {
        if (btn.textContent?.trim() === 'Insert' || btn.textContent?.trim() === 'Insert ke Kanvas (Smart)') {
          // Excalidraw buttons might have the text inside a span
          insertBtn = btn;
        }
      });

      if (insertBtn && insertBtn.textContent?.trim() === 'Insert' && !insertBtn._hijacked) {
        insertBtn._hijacked = true;
        const origBtn = insertBtn as HTMLButtonElement;
        const newBtn = origBtn.cloneNode(true) as HTMLButtonElement;
        
        // Find text node in the new button and change it
        const btnWalk = document.createTreeWalker(newBtn, NodeFilter.SHOW_TEXT, null);
        let btnTextNode;
        while ((btnTextNode = btnWalk.nextNode())) {
          if (btnTextNode.textContent?.includes('Insert')) {
            btnTextNode.textContent = "Insert ke Kanvas (Smart)";
          }
        }
        
        origBtn.style.display = 'none';
        origBtn.parentElement?.appendChild(newBtn);

        newBtn.addEventListener('click', async (e) => {
          e.preventDefault();
          e.stopPropagation();

          // Extract text from CodeMirror or Textarea
          let text = '';
          const cmLines = document.querySelectorAll('.cm-line');
          if (cmLines.length > 0) {
            cmLines.forEach(line => {
              text += line.textContent + '\n';
            });
          } else {
            const textarea = document.querySelector('textarea') as HTMLTextAreaElement;
            if (textarea) text = textarea.value;
          }

          if (!text.trim()) return;

          // Replace text safely
          const newBtnWalk = document.createTreeWalker(newBtn, NodeFilter.SHOW_TEXT, null);
          let nbn;
          while ((nbn = newBtnWalk.nextNode())) {
            if (nbn.textContent?.includes('Insert')) {
              nbn.textContent = "Menghitung Posisi...";
            }
          }
          newBtn.disabled = true;

          // Generate elements
          let newElements: any[] = [];
          let newFiles: any = {};
          
          try {
            const isMarkdown = 
              text.includes("# ") || 
              text.includes("```") || 
              text.includes("> ") || 
              text.includes("|") || 
              /^([-*]|\d+\.)\s/m.test(text);

            if (isMarkdown) {
              const { parseMarkdownToExcalidraw } = await import("../src/markdownToExcalidraw");
              newElements = await parseMarkdownToExcalidraw(text);
            } else {
              const output = await parseMermaid(text);
              const res = graphToExcalidraw(output, { fontSize: DEFAULT_FONT_SIZE });
              newElements = convertToExcalidrawElements(res.elements);
              newFiles = res.files;
            }
          } catch (err) {
            console.error(err);
            alert("Gagal memparsing: " + String(err));
            
            const errWalk = document.createTreeWalker(newBtn, NodeFilter.SHOW_TEXT, null);
            let ebn;
            while ((ebn = errWalk.nextNode())) {
              if (ebn.textContent?.includes('Menghitung')) {
                ebn.textContent = "Insert ke Kanvas (Smart)";
              }
            }
            newBtn.disabled = false;
            return;
          }

          // Find free space logic
          const currentElements = api.getSceneElements();
          let maxY = 0;
          currentElements.forEach(el => {
            if (el.y + el.height > maxY) {
              maxY = el.y + el.height;
            }
          });

          // Shift new elements down by (maxY + 100) if canvas is not empty
          const offset = currentElements.length > 0 ? maxY + 100 : 0;
          const shiftedElements = newElements.map(el => {
            return { ...el, y: el.y + offset };
          });

          // Update scene
          api.updateScene({
            elements: [...currentElements, ...shiftedElements]
          });
          
          if (Object.keys(newFiles).length > 0) {
            api.addFiles(Object.values(newFiles));
          }

          // Close modal by finding close buttons
          const closeBtns = document.querySelectorAll('.Modal__close, [aria-label="Close"], .excalidraw-modal__close');
          closeBtns.forEach(btn => (btn as HTMLButtonElement).click());
        });
      }
    }, 100);

    return () => clearInterval(interval);
  }, [api]);
};
